import React, { useState } from "react";
import { Link } from "react-router-dom";
import logo from "../assets/logo2.png"; // update path if needed

function SocialIcon({ children, href, label }) {
  return (
    <a
      href={href}
      aria-label={label}
      target="_blank"
      rel="noopener noreferrer"
      className="group relative inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/5 ring-1 ring-white/10 transition-all duration-300 hover:bg-amber-500/10 hover:ring-amber-500/50 hover:-translate-y-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
    >
      <div className="absolute inset-0 rounded-full bg-amber-400/0 transition-colors duration-300 group-hover:bg-amber-400/10 blur-md"></div>
      <span className="relative z-10 text-slate-300 transition-colors duration-300 group-hover:text-amber-400">
        {children}
      </span>
    </a>
  );
}

function BrandLogo({ src, alt = "ShowTimeBro", heightClass = "h-10 md:h-12" }) {
  return (
    <div className="group relative flex items-center gap-3">
      <div className="absolute -inset-2 rounded-xl bg-amber-500/20 opacity-0 blur-lg transition duration-500 group-hover:opacity-100"></div>
      {src ? (
        <img
          src={src}
          alt={alt}
          className={`relative z-10 ${heightClass} w-auto object-contain drop-shadow-[0_0_15px_rgba(245,158,11,0.2)]`}
          loading="lazy"
        />
      ) : (
        <div
          className={`relative z-10 ${heightClass} aspect-square grid place-items-center rounded-2xl bg-white/5 ring-1 ring-white/10`}
          aria-hidden="true"
        >
          <span className="text-xl">🎟️</span>
        </div>
      )}
      <span className="sr-only">{alt}</span>
    </div>
  );
}

export default function Footer() {
  const year = new Date().getFullYear();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null); // { type, text }
  const [submitting, setSubmitting] = useState(false);
  const [hp, setHp] = useState("");

  const validateEmail = (e) => {
    const re = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    return re.test(e);
  };

  const handleSubscribe = async (ev) => {
    ev.preventDefault();
    setStatus(null);

    if (hp) return;

    if (!email || !validateEmail(email)) {
      setStatus({ type: "error", text: "Please enter a valid email address." });
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch("/api/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error("Subscription failed");
      setStatus({ type: "success", text: "Subscribed — check your inbox for confirmation." });
      setEmail("");
    } catch (err) {
      setStatus({ type: "error", text: "Something went wrong. Please try again later." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="relative mt-auto text-slate-400 overflow-hidden bg-slate-950 font-sans">
      {/* Premium background effects */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-black pointer-events-none" />
      <div className="absolute top-0 left-1/4 w-1/2 h-[500px] bg-amber-500/5 blur-[120px] rounded-full pointer-events-none transform -translate-y-1/2"></div>
      
      {/* Top glowing divider */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/20 to-transparent shadow-[0_0_15px_rgba(245,158,11,0.3)]" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        
        {/* Top area */}
        <div className="flex flex-col items-start justify-between gap-6 border-b border-white/10 py-12 md:flex-row md:items-center">
          <Link to="/" className="flex items-center gap-3">
            <BrandLogo src={logo} alt="ShowTimeBro" />
          </Link>
          <div className="flex items-center gap-4 text-sm font-medium text-slate-400">
            <span className="flex items-center gap-2">
              <div className="relative flex h-3 w-3 items-center justify-center">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500"></span>
              </div>
              All systems operational
            </span>
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-2 md:grid-cols-12 gap-8 py-16 text-sm lg:gap-12">
          
          <nav aria-labelledby="discover-heading" className="md:col-span-3">
            <h3 id="discover-heading" className="text-xs font-bold uppercase tracking-widest text-slate-100">Discover</h3>
            <ul className="mt-6 space-y-4">
              <li><a href="/#featured" className="group inline-flex items-center gap-2 transition-colors hover:text-amber-400"><span className="h-px w-3 bg-amber-400/0 transition-all duration-300 group-hover:bg-amber-400 group-hover:w-5"></span>Featured</a></li>
              <li><a href="/#trending" className="group inline-flex items-center gap-2 transition-colors hover:text-amber-400"><span className="h-px w-3 bg-amber-400/0 transition-all duration-300 group-hover:bg-amber-400 group-hover:w-5"></span>Trending</a></li>
              <li><a href="/#weekend" className="group inline-flex items-center gap-2 transition-colors hover:text-amber-400"><span className="h-px w-3 bg-amber-400/0 transition-all duration-300 group-hover:bg-amber-400 group-hover:w-5"></span>This Weekend</a></li>
            </ul>
          </nav>

          <nav aria-labelledby="company-heading" className="md:col-span-3">
            <h3 id="company-heading" className="text-xs font-bold uppercase tracking-widest text-slate-100">Company</h3>
            <ul className="mt-6 space-y-4">
              <li><Link to="/about" className="group inline-flex items-center gap-2 transition-colors hover:text-amber-400"><span className="h-px w-3 bg-amber-400/0 transition-all duration-300 group-hover:bg-amber-400 group-hover:w-5"></span>About Us</Link></li>
              <li><Link to="/contact" className="group inline-flex items-center gap-2 transition-colors hover:text-amber-400"><span className="h-px w-3 bg-amber-400/0 transition-all duration-300 group-hover:bg-amber-400 group-hover:w-5"></span>Contact</Link></li>
              <li><Link to="/faq" className="group inline-flex items-center gap-2 transition-colors hover:text-amber-400"><span className="h-px w-3 bg-amber-400/0 transition-all duration-300 group-hover:bg-amber-400 group-hover:w-5"></span>FAQ</Link></li>
            </ul>
          </nav>

          <nav aria-labelledby="legal-heading" className="md:col-span-3">
            <h3 id="legal-heading" className="text-xs font-bold uppercase tracking-widest text-slate-100">Legal</h3>
            <ul className="mt-6 space-y-4">
              <li><Link to="/terms-of-use" className="group inline-flex items-center gap-2 transition-colors hover:text-amber-400"><span className="h-px w-3 bg-amber-400/0 transition-all duration-300 group-hover:bg-amber-400 group-hover:w-5"></span>Terms of Use</Link></li>
              <li><Link to="/privacy-policy" className="group inline-flex items-center gap-2 transition-colors hover:text-amber-400"><span className="h-px w-3 bg-amber-400/0 transition-all duration-300 group-hover:bg-amber-400 group-hover:w-5"></span>Privacy Policy</Link></li>
              <li><Link to="/shipping" className="group inline-flex items-center gap-2 transition-colors hover:text-amber-400"><span className="h-px w-3 bg-amber-400/0 transition-all duration-300 group-hover:bg-amber-400 group-hover:w-5"></span>Shipping & Delivery</Link></li>
              <li><Link to="/cancellation" className="group inline-flex items-center gap-2 transition-colors hover:text-amber-400"><span className="h-px w-3 bg-amber-400/0 transition-all duration-300 group-hover:bg-amber-400 group-hover:w-5"></span>Cancellation & Refund</Link></li>
            </ul>
          </nav>

          {/* Newsletter - spanning larger area */}
          <div aria-labelledby="newsletter-heading" className="col-span-2 md:col-span-3">
            <h3 id="newsletter-heading" className="text-xs font-bold uppercase tracking-widest text-slate-100">Stay in the loop</h3>
            <p className="mt-6 text-slate-400 leading-relaxed">
              Curated experiences and exclusive offers delivered straight to your inbox.
            </p>

            <form
              onSubmit={handleSubscribe}
              className="mt-6 relative flex items-center overflow-hidden rounded-full bg-white/5 ring-1 ring-white/10 focus-within:ring-amber-500/50 focus-within:bg-white/10 transition-all duration-300 shadow-inner"
              aria-label="Subscribe to newsletter"
            >
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-transparent px-5 py-3 text-sm text-slate-100 placeholder:text-slate-500 outline-none"
                placeholder="Enter your email"
                aria-label="Email address"
                required
              />
              <input type="text" name="website" value={hp} onChange={(e) => setHp(e.target.value)} className="sr-only" tabIndex="-1" autoComplete="off" aria-hidden="true" />
              
              <button
                className="absolute right-1.5 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 px-5 py-2 text-xs font-bold text-slate-900 shadow-lg shadow-amber-500/20 transition-all hover:scale-105 hover:from-amber-300 hover:to-amber-400 disabled:opacity-50 disabled:hover:scale-100"
                type="submit"
                disabled={submitting}
              >
                {submitting ? "..." : "Subscribe"}
              </button>
            </form>

            <div className="mt-4 flex items-center gap-2 text-[11px] font-medium text-slate-500">
              <svg className="h-3.5 w-3.5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              No spam. Unsubscribe anytime.
            </div>

            <div className="mt-3 h-4" aria-live="polite">
              {status && (
                <p className={`text-xs font-medium animate-pulse ${status.type === "success" ? "text-emerald-400" : "text-rose-400"}`}>
                  {status.text}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col-reverse items-center justify-between gap-6 border-t border-white/10 py-8 text-xs font-medium md:flex-row">
          <div className="flex items-center gap-1.5">
            <span className="text-slate-500">© {year} ShowTimeBro. All rights reserved.</span>
            <span className="text-slate-700 hidden sm:inline">•</span>
            <span className="text-slate-400 hidden sm:inline flex items-center gap-1">Made with <span className="text-rose-500 animate-pulse">❤️</span> in India</span>
          </div>

          <div className="flex items-center gap-4">
            <SocialIcon href="https://twitter.com" label="Twitter">
              <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="currentColor"><path d="M23 4.5c-.8.4-1.7.7-2.6.8a4.5 4.5 0 0 0-7.8 4.1 12.8 12.8 0 0 1-9.3-4.7A4.5 4.5 0 0 0 4 11a4.4 4.4 0 0 1-2-.5 4.5 4.5 0 0 0 3.6 4.4 4.6 4.6 0 0 1-2 .1 4.5 4.5 0 0 0 4.2 3.1A9 9 0 0 1 2 20.6a12.7 12.7 0 0 0 6.9 2c8.3 0 12.8-6.9 12.8-12.8v-.6c.9-.6 1.6-1.4 2.3-2.3z" /></svg>
            </SocialIcon>
            <SocialIcon href="https://facebook.com" label="Facebook">
              <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="currentColor"><path d="M22 12a10 10 0 1 0-11.6 9.9v-7h-2.3V12h2.3V9.7c0-2.3 1.4-3.6 3.5-3.6 1 0 2 .2 2 .2v2.2h-1.1c-1.1 0-1.5.7-1.5 1.4V12h2.6l-.4 2.9h-2.2v7A10 10 0 0 0 22 12z" /></svg>
            </SocialIcon>
            <SocialIcon href="https://www.instagram.com/showtimebro_official" label="Instagram">
              <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="currentColor"><path d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5zm5 5.3A4.7 4.7 0 1 0 12 17.7 4.7 4.7 0 0 0 12 7.3zm6.1-.8a1.1 1.1 0 1 0 0 2.2 1.1 1.1 0 0 0 0-2.2zM12 9.3a3.3 3.3 0 1 1 0 6.7 3.3 3.3 0 0 1 0-6.7z" /></svg>
            </SocialIcon>
          </div>
        </div>
      </div>
    </footer>
  );
}
